'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface MapCategory {
  id: string;
  name: string;
  icon: string;
  color: string;
  mapSrc: string;
}

interface InteractiveMapProps {
  className?: string;
}

const InteractiveMap = ({ className = '' }: InteractiveMapProps) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const categories: MapCategory[] = [
    {
      id: 'all',
      name: 'All Locations',
      icon: 'MapPinIcon',
      color: 'bg-primary',
      mapSrc: 'https://maps.google.com/maps?q=12.892138376189589,77.60149796028242&z=16&output=embed',
    },
    {
      id: 'corporate',
      name: 'Corporate Hubs',
      icon: 'BuildingOfficeIcon',
      color: 'bg-secondary',
      mapSrc: 'https://maps.google.com/maps?q=tech+parks+near+IIM+Bangalore+Bannerghatta+Road&ll=12.892138,77.601498&z=14&output=embed',
    },
    {
      id: 'dining',
      name: 'Dining',
      icon: 'BuildingStorefrontIcon',
      color: 'bg-accent',
      mapSrc: 'https://maps.google.com/maps?q=restaurants+near+Bilekahalli+Bangalore&ll=12.892138,77.601498&z=15&output=embed',
    },
    {
      id: 'education',
      name: 'Education',
      icon: 'AcademicCapIcon',
      color: 'bg-success',
      mapSrc: 'https://maps.google.com/maps?q=schools+colleges+near+Bannerghatta+Road+Bangalore&ll=12.892138,77.601498&z=14&output=embed',
    },
    {
      id: 'entertainment',
      name: 'Entertainment',
      icon: 'FilmIcon',
      color: 'bg-warning',
      mapSrc: 'https://maps.google.com/maps?q=malls+entertainment+near+Bannerghatta+Road+Bangalore&ll=12.892138,77.601498&z=14&output=embed',
    },
    {
      id: 'transport',
      name: 'Transport',
      icon: 'TruckIcon',
      color: 'bg-error',
      mapSrc: 'https://maps.google.com/maps?q=bus+stop+metro+near+Bilekahalli+Bangalore&ll=12.892138,77.601498&z=14&output=embed',
    },
  ];

  const activeCategory = categories.find((c) => c.id === selectedCategory) || categories[0];

  return (
    <section className={`py-16 lg:py-24 bg-background ${className}`}>
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl lg:text-5xl font-bold text-text-primary mb-4">
            Interactive Neighborhood Map
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Explore everything within reach of Proximus. Filter by category to discover corporate offices, dining destinations, educational institutions, and entertainment venues.
          </p>
        </div>

        <div className="mb-8 flex flex-wrap gap-3 justify-center">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg font-medium transition-all duration-300 ${
                selectedCategory === category.id
                  ? `${category.color} text-white shadow-elevated`
                  : 'bg-card text-text-secondary hover:bg-muted'
              }`}
            >
              <Icon name={category.icon as any} size={20} />
              <span>{category.name}</span>
            </button>
          ))}
        </div>

        <div className="bg-card rounded-2xl shadow-premium overflow-hidden border border-border">
          <div className="aspect-video w-full">
            <iframe
              key={selectedCategory}
              width="100%"
              height="100%"
              loading="lazy"
              title={`Proximus Serviced Apartments - ${activeCategory.name}`}
              referrerPolicy="no-referrer-when-downgrade"
              src={activeCategory.mapSrc}
              className="w-full h-full"
            />
          </div>

          <div className="p-6 bg-white">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="MapPinIcon" size={24} className="text-primary" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-text-primary mb-2">
                  Proximus Serviced Apartments
                </h3>
                <p className="text-text-secondary mb-4">
                  139/1, 2A, 1, 1st Main Rd, Sarvabhoumanagar, Sundar Ram Shetty Nagar, Bilekahalli, Bengaluru, Karnataka 560076, India
                </p>
                <div className="flex flex-wrap gap-4">
                  <a
                    href="https://maps.google.com/?q=12.892138376189589,77.60149796028242"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 text-sm text-primary hover:underline font-medium"
                  >
                    <Icon name="ArrowTopRightOnSquareIcon" size={16} />
                    <span>Open in Google Maps</span>
                  </a>
                  <div className="flex items-center space-x-2 text-sm text-text-secondary">
                    <Icon name="ClockIcon" size={16} />
                    <span>20 mins to Electronic City</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-text-secondary">
                    <Icon name="BuildingOfficeIcon" size={16} />
                    <span>1 min to IIM Bangalore</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-text-secondary">
                    <Icon name="TruckIcon" size={16} />
                    <span>60-75 mins to Airport</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InteractiveMap;